import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { ArrowLeft, Phone, MessageCircle, Mail, IndianRupee, FileText, Clock, CheckCircle, AlertCircle, Brain } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import AIAnalysisTab from '../components/AIAnalysisTab';
import ProposalTab from '../components/ProposalTab';
import CallButton from '../components/CallButton';
import LenderStagesTab from '../components/LenderStagesTab';
import WhatsAppButton from '../components/WhatsAppButton';
import { PaymentModal, PromiseModal } from '../components/PaymentModal';
import { LeadPaymentLinkModal, ClientPaymentLinkModal } from '../components/PaymentLinkModal';

// Quick Legal Task component
function QuickLegalTask({ leadId }) {
  const [taskTypes, setTaskTypes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [form, setForm] = useState({ taskType:'', lenderName:'', priority:'normal', description:'', noticeRefNo:'', noticeDate:'' });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = React.useRef();
  const set = (k, v) => setForm(p => ({...p,[k]:v}));

  const loadTasks = () => api.get('/legal', { params:{ leadId } }).then(r => setTasks(r.data));

  useEffect(() => {
    api.get('/legal/types').then(r => setTaskTypes(r.data));
    loadTasks();
  }, [leadId]);

  const save = async () => {
    if (!form.taskType) return toast.error('Task type select karo');
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('leadId', leadId);
      Object.entries(form).forEach(([k,v]) => fd.append(k,v));
      if (file) fd.append('noticePdf', file);
      await api.post('/legal', fd, { headers:{ 'Content-Type':'multipart/form-data' } });
      toast.success('Legal task created — legal team ko assign ho gaya!');
      setForm({ taskType:'', lenderName:'', priority:'normal', description:'', noticeRefNo:'', noticeDate:'' });
      setFile(null);
      loadTasks();
    } catch(e) { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const CATS = [
    { key:'notice',    label:'⚖️ Notice Replies' },
    { key:'complaint', label:'📋 Complaints' },
    { key:'harassment',label:'🛡️ Harassment' },
    { key:'draft',     label:'📄 Drafts' },
  ];

  return (
    <div className="grid" style={{ gridTemplateColumns:'1fr 1fr', gap:20 }}>
      <div className="card">
        <h3 style={{ fontSize:14, marginBottom:16 }}>Create Legal Task</h3>
        <div className="form-group">
          <label className="form-label">Task Type *</label>
          <select className="form-control" value={form.taskType} onChange={e => set('taskType', e.target.value)}>
            <option value="">Select...</option>
            {CATS.map(cat => (
              <optgroup key={cat.key} label={cat.label}>
                {taskTypes.filter(t => t.category === cat.key).map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </optgroup>
            ))}
          </select>
        </div>
        <div className="grid grid-2">
          <div className="form-group">
            <label className="form-label">Lender/Bank</label>
            <input className="form-control" value={form.lenderName} onChange={e => set('lenderName', e.target.value)} placeholder="HDFC Bank..." />
          </div>
          <div className="form-group">
            <label className="form-label">Priority</label>
            <select className="form-control" value={form.priority} onChange={e => set('priority', e.target.value)}>
              <option value="urgent">🔴 Urgent</option>
              <option value="high">🟠 High</option>
              <option value="normal">🔵 Normal</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Notice Ref No.</label>
            <input className="form-control" value={form.noticeRefNo} onChange={e => set('noticeRefNo', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Notice Date</label>
            <input className="form-control" type="date" value={form.noticeDate} onChange={e => set('noticeDate', e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Description / Instructions</label>
          <textarea className="form-control" rows={2} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Notice ke key points..." />
        </div>
        <div className="form-group">
          <label className="form-label">Upload Notice PDF</label>
          <div style={{ border:'2px dashed var(--border)', borderRadius:8, padding:12, textAlign:'center', cursor:'pointer', background: file ? '#f0fdf4':'var(--surface)' }} onClick={() => fileRef.current.click()}>
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.png" style={{display:'none'}} onChange={e => setFile(e.target.files[0])} />
            {file ? <div style={{fontSize:13,color:'var(--success)',fontWeight:600}}>✓ {file.name}</div> : <div style={{fontSize:12,color:'var(--text-muted)'}}>Click to upload PDF</div>}
          </div>
        </div>
        <button className="btn btn-primary w-full" style={{justifyContent:'center'}} onClick={save} disabled={saving}>
          {saving ? <span className="spinner"/> : '⚖️ Assign to Legal Team'}
        </button>
      </div>
      <div>
        <h3 style={{fontSize:14, marginBottom:12}}>Existing Legal Tasks</h3>
        {tasks.length === 0 ? (
          <div className="empty-state" style={{padding:24}}><h3>No tasks yet</h3></div>
        ) : tasks.map(t => (
          <div key={t.id} className="card mb-8" style={{padding:'10px 14px'}}>
            <div style={{fontWeight:600,fontSize:13}}>{t.taskTypeLabel}</div>
            <div style={{fontSize:12,color:'var(--text-muted)',marginTop:3}}>
              {t.lenderName && `🏦 ${t.lenderName} · `}
              <span className={`badge ${t.status==='completed'?'badge-green':t.status==='pending'?'badge-yellow':'badge-blue'}`}>{t.status}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const STAGES = ['New Lead','Initial Discussion','Credit Report Pending','Credit Report Received','Analysis Shared','Lender Confirmation','Proposal Sent','Enrollment Follow-up','Payment Expected','Enrolled','Active Client','Dropped'];

function fmt(n) { return n ? '₹' + Number(n).toLocaleString('en-IN') : '—'; }
function timeAgo(d) {
  if (!d) return '';
  const diff = Date.now() - new Date(d);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const ACTION_ICONS = {
  lead_created: '🆕', stage_changed: '🔄', call: '📞', followup_scheduled: '📅',
  promise_set: '🤝', enrolled: '✅', retainer_received: '💰', analysis_fee_paid: '💳',
  internal_analysis_generated: '🧠', client_analysis_generated: '📊', proposal_generated: '📄',
  note: '💬', reassigned: '🔀', approval_requested: '⏳'
};

export default function LeadDetail({ leadId, onBack, users }) {
  const [lead, setLead] = useState(null);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [stage, setStage] = useState('');
  const [showPayment, setShowPayment] = useState(false);
  const [showPromise, setShowPromise] = useState(false);
  const [showPaymentLink, setShowPaymentLink] = useState(false);
  const [followupDate, setFollowupDate] = useState('');
  const [followupTime, setFollowupTime] = useState('10:00');
  const [followupReason, setFollowupReason] = useState('');
  const { user } = useAuth();

  const load = () => {
    api.get(`/leads/${leadId}`).then(r => {
      setLead(r.data);
      setStage(r.data.stage);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [leadId]);

  const updateStage = async (newStage) => {
    await api.patch(`/leads/${leadId}`, { stage: newStage });
    setStage(newStage);
    load();
    toast.success('Stage updated');
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/leads/${leadId}/activity`, { type: 'note', description: note });
    setNote('');
    load();
    toast.success('Note added');
  };

  const addFollowup = async () => {
    if (!followupDate) return toast.error('Select date');
    await api.post(`/leads/${leadId}/followup`, { date: followupDate, time: followupTime, reason: followupReason });
    setFollowupDate(''); setFollowupReason('');
    load();
    toast.success('Follow-up scheduled');
  };

  const openWhatsApp = (mobile) => {
    window.open(`https://wa.me/91${mobile}`, '_blank');
    api.post(`/leads/${leadId}/activity`, { type: 'whatsapp_opened', description: 'WhatsApp opened' });
  };

  if (loading) return <div className="page" style={{ textAlign: 'center', paddingTop: 80 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;
  if (!lead) return null;

  const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'lenders', label: '🏦 Lender Stages', roles: ['admin','tl','consultant'], enrolledOnly: true },
    { key: 'proposal', label: '📄 Proposal', roles: ['admin','tl','consultant'], enrolledExclude: true },
    { key: 'ai', label: '🧠 AI Analysis', roles: ['admin','tl','consultant'] },
    { key: 'legal', label: '⚖️ Legal Task', roles: ['admin','tl','consultant'], enrolledOnly: true },
    { key: 'payments', label: 'Payments' },
    { key: 'followups', label: 'Follow-ups' },
    { key: 'notes', label: 'Notes & Activity' },
    { key: 'timeline', label: 'Timeline' }
  ].filter(t =>
    (!t.roles || t.roles.includes(user?.role)) &&
    (!t.enrolledOnly || lead.enrolled) &&
    (!t.enrolledExclude || !lead.enrolled)
  );

  return (
    <div className="page">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 24 }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}><ArrowLeft size={14} /> Back</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: 20 }}>{lead.name}</h2>
            {lead.enrolled && <span className="badge badge-green">✓ Enrolled</span>}
            {lead.stage === 'Active Client' && <span className="badge badge-navy">Active Client</span>}
            {lead.overdueRetainer && (
              <span className="badge badge-red" title="Retainer payment overdue this month">🔴 Retainer Overdue</span>
            )}
            {lead.pendingPayments > 0 && (
              <span className="badge badge-yellow" title={`${lead.pendingPayments} payment(s) pending manager verification`}>⏳ {lead.pendingPayments} Payment Pending Verify</span>
            )}
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>{user?.role === 'legal' ? '🔒 Contact Hidden' : lead.mobile} · {lead.city} · {lead.employmentStatus}</div>
        </div>
        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <CallButton leadId={lead.id} mobile={lead.mobile} name={lead.name} style={{ display: user?.role === 'legal' ? 'none' : '' }} />
          <WhatsAppButton leadId={lead.id} mobile={lead.mobile} style={{ display: user?.role === 'legal' ? 'none' : '' }} />
          <button className="btn btn-gold btn-sm" onClick={() => setShowPayment(true)}><IndianRupee size={14} /> Payment</button>
          <button className="btn btn-sm" style={{ background: '#10b981', color: 'white' }} onClick={() => setShowPaymentLink(true)}>🔗 Payment Link</button>
          <button className="btn btn-primary btn-sm" onClick={() => setTab('ai')}><Brain size={14} /> AI Analysis</button>
        </div>
      </div>

      {/* Stage selector */}
      <div className="card mb-16" style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>STAGE:</span>
          <select className="form-control" style={{ width: 220 }} value={stage} onChange={e => updateStage(e.target.value)}>
            {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowPromise(true)}>🤝 Set Promise</button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {TABS.map(t => <button key={t.key} className={`tab-btn ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>{t.label}</button>)}
      </div>

      {/* Overview Tab */}
      {tab === 'overview' && (
        <div className="grid grid-2" style={{ gap: 20 }}>
          <div>
            <div className="card mb-16">
              <h3 style={{ fontSize: 14, marginBottom: 16 }}>Financial Summary</h3>
              <table style={{ width: '100%' }}>
                <tbody>
                  {[
                    ['Total Liability', fmt(lead.totalLiability), true],
                    ['Personal Loan', fmt(lead.personalLoan)],
                    ['Credit Card', fmt(lead.creditCard)],
                    lead.monthlyFee && ['Monthly Fee', fmt(lead.monthlyFee)],
                  ].filter(Boolean).map(([k, v, bold]) => (
                    <tr key={k}>
                      <td style={{ padding: '7px 0', fontSize: 13, color: 'var(--text-muted)' }}>{k}</td>
                      <td style={{ padding: '7px 0', fontSize: 13, fontWeight: bold ? 700 : 600, textAlign: 'right' }}>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="card mb-16">
              <h3 style={{ fontSize: 14, marginBottom: 16 }}>Contact Details</h3>
              {[
                ['Mobile', user?.role === 'legal' ? '🔒 Hidden' : lead.mobile],
                ['Email', user?.role === 'legal' ? '🔒 Hidden' : lead.email],
                ['City', lead.city],
                ['Alt. WhatsApp', user?.role === 'legal' ? '🔒 Hidden' : (lead.alternateWhatsApp || '—')],
                ['Employment', lead.employmentStatus],
                ['Source', lead.source],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                  <span style={{ color: 'var(--text-muted)' }}>{k}</span>
                  <span style={{ fontWeight: 500 }}>{v || '—'}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            {lead.internalAnalysis && (
              <div className="card mb-16" style={{ borderLeft: '3px solid var(--gold)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                  <Brain size={16} style={{ color: 'var(--gold)' }} />
                  <h3 style={{ fontSize: 14 }}>AI Insights (Internal)</h3>
                </div>
                <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                  <div style={{ flex: 1, textAlign: 'center', padding: '10px 8px', background: 'var(--surface)', borderRadius: 8 }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy)' }}>{lead.internalAnalysis.enrollmentProbability}%</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Enrollment Prob.</div>
                  </div>
                  <div style={{ flex: 1, textAlign: 'center', padding: '10px 8px', background: 'var(--surface)', borderRadius: 8 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: lead.internalAnalysis.leadTemperature === 'Hot' ? 'var(--danger)' : lead.internalAnalysis.leadTemperature === 'Warm' ? 'var(--warning)' : 'var(--accent)' }}>
                      {lead.internalAnalysis.leadTemperature === 'Hot' ? '🔥' : lead.internalAnalysis.leadTemperature === 'Warm' ? '🌤️' : '❄️'} {lead.internalAnalysis.leadTemperature}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Lead Temp.</div>
                  </div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>Next Best Action:</div>
                <div style={{ fontSize: 13, fontWeight: 500, padding: '8px 10px', background: '#f0fdf4', borderRadius: 6, color: '#065f46' }}>
                  {lead.internalAnalysis.nextBestAction}
                </div>
              </div>
            )}

            {lead.promiseDate && (
              <div className="card mb-16" style={{ borderLeft: '3px solid var(--accent)' }}>
                <h3 style={{ fontSize: 14, marginBottom: 12 }}>🤝 Promise Tracker</h3>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Promise Date</div><div style={{ fontWeight: 600 }}>{lead.promiseDate}</div></div>
                  {lead.promiseAmount && <div style={{ textAlign: 'right' }}><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Expected Amount</div><div style={{ fontWeight: 700, color: 'var(--accent)' }}>{fmt(lead.promiseAmount)}</div></div>}
                </div>
                {lead.promiseRemarks && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>{lead.promiseRemarks}</div>}
              </div>
            )}

            {lead.description && (
              <div className="card">
                <h3 style={{ fontSize: 14, marginBottom: 8 }}>Description</h3>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6 }}>{lead.description}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Lender Stages Tab - enrolled clients only */}
      {tab === 'lenders' && lead.enrolled && <LenderStagesTab lead={lead} onUpdate={load} />}

      {/* Proposal Tab - non-enrolled leads only */}
      {tab === 'proposal' && !lead.enrolled && <ProposalTab lead={lead} onUpdate={load} />}

      {/* AI Analysis Tab */}
      {tab === 'ai' && <AIAnalysisTab lead={lead} onUpdate={load} />}

      {/* Legal Task Tab - enrolled clients only */}
      {tab === 'legal' && lead.enrolled && <QuickLegalTask leadId={lead.id} />}

      {/* Legal Task Tab */}
      {tab === 'legal' && <QuickLegalTask leadId={lead.id} leadName={lead.name} />}

      {/* Payments Tab */}
      {tab === 'payments' && (
        <div>
          <div className="flex-between mb-16">
            <h3 style={{ fontSize: 15 }}>Payment History</h3>
            <button className="btn btn-gold btn-sm" onClick={() => setShowPayment(true)}><IndianRupee size={14} /> Record Payment</button>
          </div>
          {lead.payments?.length === 0 ? (
            <div className="empty-state"><IndianRupee size={32} /><h3>No payments yet</h3></div>
          ) : (
            <div className="card" style={{ padding: 0 }}>
              <table>
                <thead><tr><th>Date</th><th>Type</th><th>Amount</th><th>Mode</th><th>Notes</th></tr></thead>
                <tbody>
                  {lead.payments?.map(p => (
                    <tr key={p.id}>
                      <td style={{ fontSize: 12 }}>{new Date(p.createdAt).toLocaleDateString('en-IN')}</td>
                      <td><span className={`badge ${p.type === 'enrollment' ? 'badge-green' : p.type === 'retainer' ? 'badge-blue' : p.type === 'analysis_fee' ? 'badge-purple' : 'badge-gray'}`}>{p.type}</span></td>
                      <td style={{ fontWeight: 700 }}>{fmt(p.amount)}</td>
                      <td style={{ fontSize: 12 }}>{p.mode}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Follow-ups Tab */}
      {tab === 'followups' && (
        <div className="grid grid-2" style={{ gap: 20 }}>
          <div className="card">
            <h3 style={{ fontSize: 14, marginBottom: 16 }}>Schedule Follow-up</h3>
            <div className="form-group">
              <label className="form-label">Date</label>
              <input className="form-control" type="date" value={followupDate} onChange={e => setFollowupDate(e.target.value)} min={new Date().toISOString().split('T')[0]} />
            </div>
            <div className="form-group">
              <label className="form-label">Time</label>
              <input className="form-control" type="time" value={followupTime} onChange={e => setFollowupTime(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Reason</label>
              <select className="form-control" value={followupReason} onChange={e => setFollowupReason(e.target.value)}>
                <option value="">Select reason</option>
                {['Credit Report Pending','Proposal Review','Payment Follow-up','General Check-in','Promise Reminder','Enrollment Discussion'].map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <button className="btn btn-primary w-full" style={{ justifyContent: 'center' }} onClick={addFollowup}>Schedule Follow-up</button>
          </div>
          <div>
            <h3 style={{ fontSize: 14, marginBottom: 12 }}>Scheduled Follow-ups</h3>
            {lead.followUps?.length === 0 ? (
              <div className="empty-state"><Clock size={32} /><h3>No follow-ups scheduled</h3></div>
            ) : lead.followUps?.map(fu => (
              <div key={fu.id} className="card mb-8">
                <div className="flex-between">
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{fu.date} at {fu.time}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fu.reason}</div>
                  </div>
                  {fu.done ? <span className="badge badge-green">Done</span> : new Date(`${fu.date}T${fu.time}`) < new Date() ? <span className="badge badge-red">Overdue</span> : <span className="badge badge-blue">Upcoming</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notes Tab */}
      {tab === 'notes' && (
        <div>
          <div className="card mb-16">
            <div style={{ display: 'flex', gap: 10 }}>
              <textarea className="form-control" style={{ flex: 1 }} placeholder="Add a note or call remark..." value={note} onChange={e => setNote(e.target.value)} rows={2} />
              <button className="btn btn-primary" onClick={addNote}>Add</button>
            </div>
          </div>
          {lead.timeline?.filter(t => ['note','call','whatsapp_opened'].includes(t.action)).map(t => (
            <div key={t.id} className="card mb-8" style={{ padding: '12px 16px' }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <span>{ACTION_ICONS[t.action] || '📝'}</span>
                <div>
                  <div style={{ fontSize: 13 }}>{t.description}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{t.userName} · {timeAgo(t.createdAt)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Timeline Tab */}
      {tab === 'timeline' && (
        <div className="card">
          <h3 style={{ fontSize: 14, marginBottom: 20 }}>Full Activity Timeline</h3>
          <div className="timeline">
            {lead.timeline?.length === 0 ? (
              <div className="empty-state"><Clock size={32} /><h3>No activity yet</h3></div>
            ) : lead.timeline?.map(item => (
              <div key={item.id} className="timeline-item">
                <div className="timeline-dot">{ACTION_ICONS[item.action] || '📋'}</div>
                <div className="timeline-content">
                  <div className="timeline-action">{item.description}</div>
                  <div className="timeline-time">{item.userName} · {new Date(item.createdAt).toLocaleString('en-IN')}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showPayment && <PaymentModal leadId={lead.id} leadName={lead.name} onClose={() => setShowPayment(false)} onSave={() => { setShowPayment(false); load(); toast.success('Payment recorded!'); }} />}
      {showPromise && <PromiseModal leadId={lead.id} onClose={() => setShowPromise(false)} onSave={() => { setShowPromise(false); load(); toast.success('Promise saved!'); }} />}
      {showPaymentLink && (lead.enrolled
        ? <ClientPaymentLinkModal lead={lead} onClose={() => setShowPaymentLink(false)} />
        : <LeadPaymentLinkModal lead={lead} onClose={() => setShowPaymentLink(false)} />
      )}
    </div>
  );
}
